import { types as sdkTypes, transit } from './sdkLoader';
import config from '../config';
import Decimal from 'decimal.js';

const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const get = (path, additionalOptions = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    ...additionalOptions,
  };
  return window
    .fetch(url, options)
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          let e = new Error();
          e = Object.assign(e, data);

          throw e;
        });
      }
      return res;
    })
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    });
};

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  let status = null;
  return window
    .fetch(url, options)
    .then(async res => {
      status = res.status;
      return res;
    })
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    })
    .then(res => {
      if (status >= 400) {
        const e = new Error(res.statusText || 'Local API request failed');
        e.apiResponse = res;
        throw e;
      }
      return res;
    });
};

const put = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return window
    .fetch(url, options)
    .then(res => {
      if (res.status >= 400) {
        const e = new Error('Local API request failed');
        e.apiResponse = res;
        throw e;
      }
      return res;
    })
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

export const extendTransactionLineItems = body => {
  return post('/api/extend-transaction-line-items', body);
};

export const reofferTransactionLineItems = body => {
  return post('/api/reoffer-transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the booking data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

export const extendInitiatePrivileged = body => {
  return post('/api/extend-initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const actionPrivileged = body => {
  return post('/api/action-privileged', body);
};

export const saveUserLast4DigitsCard = body => {
  return put('/api/save-last-4-digits-card', body);
};

export const createCommission = body => {
  return post(`/api/user/create-commission-when-sign-up`, body);
};

// Admin api
export const queryTransactionsPrivileged = query => {
  const queryString = new URLSearchParams(query).toString();
  return get(`/api/admin/transactions?${queryString}`);
};

export const fetchTransactions = ({ listingId, providerId, currentPage }) => {
  return get(
    `/api/fetch-transactions?listingId=${listingId.uuid}&providerId=${providerId}&page=${currentPage}`
  );
};

export const apiRetryPayment = body => {
  return post('/api/user/retry-payment', body);
};

export const apiRenewBond = body => {
  return post('/api/user/renew-bond', body);
};

export const addAvailabilityExceptionCurrentBooking = body => {
  return post('/api/listing/add-exception', body);
}

export const removeAvailabilityExceptionCurrentBooking = body => {
  return post('/api/listing/remove-exception', body);
}

export const saveLastMessageAt = body => {
  return put('/api/save-last-message-created-at', body);
};

export const createHoldingSlotTransaction = body => {
  return post('/api/create-holding-slot-transaction', body);
}

export const declineHoldingSlotTransaction = ({ txId }) => {
  return post('/api/decline-holding-slot-transaction', { txId });
}

export const extendTransitionPrivileged = body => {
  return post('/api/extend-transition-privileged', body);
}
